import { api } from './api';

// Mirrors apna-yatri-backend src/utils/rateCardEngine.ts (the server validates; this is the wire shape).
export interface RateSlab { fromKm: number; toKm: number | null; perKm: number }
export type NightComponent = 'base' | 'distance' | 'time' | 'pickup' | 'wait' | 'longPickup';
export type NightRule =
  | { mode: 'none' }
  | { mode: 'percent'; pct: number; appliesTo: NightComponent[] }
  | { mode: 'rates'; baseFare: number | null; slabs: RateSlab[] | null };

export interface RateCard {
  schemaVersion: number;
  baseFare: number;
  slabs: RateSlab[];
  pickupCharge: number;
  driverAdditions: number;
  congestionCharges: number;
  minimumFare: number | null;
  wait: { freeMin: number; perMin: number; capAmount: number | null };
  longPickup: { freeKm: number; perKm: number; capAmount: number | null };
  time?: { perMin: number };
  night: NightRule;
}

export interface RateCardRow {
  id: number;
  rideOptionId: number;
  zoneId: number | null;
  zoneName: string | null;
  city?: string | null;
  version: number;
  isActive: boolean;
  isSeed: boolean;
  updatedBy: string | null;
  updatedAt: string;
  card: RateCard;
  valid: boolean;
  errors: string[];
}

/** Zone overrides come as summaries; the full card loads per scope (scales to thousands of zones). */
export interface ZoneCardSummary {
  id: number; zoneId: number; zoneName: string; city: string | null; version: number;
  isActive: boolean; isSeed: boolean; updatedBy: string | null; updatedAt: string;
}

export interface RateCardVehicle {
  rideOption: { id: number; type: string; label: string; name: string; available: boolean };
  global: RateCardRow | null;
  legacyCard: RateCard | null;
  zones: ZoneCardSummary[];
}

export interface RateCardList {
  enabled: boolean;
  waitBillingEnabled: boolean;
  nightWindow: { startHour: number; endHour: number };
  zones: Array<{ id: number; name: string; city: string | null; isActive: boolean }>;
  vehicles: RateCardVehicle[];
}

export interface RateCardVersion {
  id: number; rateCardId: number; version: number; card: RateCard; isActive: boolean;
  changedBy: string | null; note: string | null; createdAt: string;
}

export interface PreviewBreakdown {
  baseFare: number; perKmTotal: number; pickupCharges: number; driverAdditions: number; congestionCharges: number;
  waitingAmount: number; billableWaitMinutes: number; longPickupAmount: number; longPickupKm: number;
  minimumFareTopUp: number; nightAmount: number; grossFare: number; isNight: boolean;
  timeMinutes: number; timePerMin: number; timeAmount: number;
  distanceSlabs: Array<{ fromKm: number; toKm: number | null; perKm: number; km: number; amount: number }>;
}
export type PreviewResult =
  | { ok: true; breakdown: PreviewBreakdown; surgeFactor: number; quotedFare: number }
  | { ok: false; errors: string[] };

export const getRateCards = (): Promise<RateCardList> => api.get('/admin/rate-cards').then((r) => r.data as RateCardList);

export interface RateCardScope { row: RateCardRow | null; startingCard: RateCard | null }

export const getRateCardScope = (rideOptionId: number, zoneId: number | null): Promise<RateCardScope> =>
  api.get('/admin/rate-cards/scope', { params: { rideOptionId, zoneId: zoneId ?? '' } }).then((r) => r.data as RateCardScope);

export interface CloneInclude { rateCards: boolean; rushHour: boolean; surge: boolean; providers: boolean; subscriptions: boolean }
export interface CloneSummary {
  rateCardsCopied: number; rateCardsRemoved: number; zoneRatesCopied: number; zoneRatesRemoved: number;
  subscriptionsCopied: number; subscriptionsRemoved: number; zoneFieldsCopied: string[];
}
export const cloneZoneSettings = (targetZoneId: number, sourceZoneId: number, include: CloneInclude): Promise<CloneSummary> =>
  api.post(`/admin/zones/${targetZoneId}/clone-settings`, { sourceZoneId, include }).then((r) => r.data.summary as CloneSummary);

export const getRateCardHistory = (id: number): Promise<RateCardVersion[]> =>
  api.get(`/admin/rate-cards/${id}/history`).then((r) => r.data.versions as RateCardVersion[]);

export const saveRateCard = (body: {
  rideOptionId: number; zoneId: number | null; card: RateCard; expectedVersion: number | null; note?: string;
}): Promise<unknown> => api.put('/admin/rate-cards', body).then((r) => r.data);

export const deactivateRateCard = (id: number, expectedVersion: number): Promise<unknown> =>
  api.delete(`/admin/rate-cards/${id}`, { params: { expectedVersion } }).then((r) => r.data);

export const previewRateCard = (body: {
  card: RateCard; distanceKm: number; durationMin: number; waitMin: number; pickupKm: number; isNight: boolean; surgeFactor: number;
}): Promise<PreviewResult> => api.post('/admin/rate-cards/preview', body).then((r) => r.data as PreviewResult);
